import { TextField, TextFieldProps } from '@portals/core/src/components/TextField/TextField';
import { Field, getIn } from 'formik';
import React, { useCallback } from 'react';

import { useEmailValidator } from '../../hooks/EmailValidator/EmailValidator';
import { teeEvent } from '../../utilties/teeEvent';

type InputMode = TextFieldProps['inputMode'];
type InputType = TextFieldProps['type'];

export type NumericInputProps = Omit<TextFieldProps, 'name' | 'value' | 'pattern'> & {
  name: TextFieldProps['name'];
  helperText?: TextFieldProps['helperText'];
  inputMode?: InputMode;
  type?: InputType;
  placeholder?: TextFieldProps['placeholder'];
  required?: TextFieldProps['required'];
  t?: (key: string, options?: Record<string, unknown>) => string;
};

/**
 * Email Form Field
 */
export const EmailInput = ({
  name,
  helperText,
  type = 'email' as InputType,
  inputMode = 'email' as InputMode,
  placeholder = 'example@domain.de',
  required = false,
  slotProps,
  t,
  ...props
}: NumericInputProps): React.ReactElement => {
  const validate = useEmailValidator({ required, t });

  const fieldComponent = useCallback(
    ({ form, field, ...props }) => {
      const error = (getIn(form.touched, name) && Boolean(getIn(form.errors, name))) || props.error;
      const onBlur = teeEvent(props.onBlur, field.onBlur);
      const onChange = teeEvent(props.onChange, field.onChange);
      const errorText = error ? getIn(form.errors, name) : helperText;

      return (
        <TextField
          helperText={errorText || helperText}
          {...field}
          {...props}
          error={error}
          onBlur={onBlur}
          onChange={onChange}
        />
      );
    },
    [helperText, name]
  );

  return (
    <Field
      component={fieldComponent}
      name={name}
      validate={validate}
      placeholder={placeholder}
      required={required}
      slotProps={{
        htmlInput: {
          inputMode,
          type,
          required,
        },
        ...slotProps,
      }}
      {...props}
    />
  );
};
